import {
  COMPANY_HOME_URL,
  MAIA_HOME_URL,
  PANEL_LEAD_URL,
  WHATSAPP_LANDING_MARKDOWN_URL,
  WHATSAPP_LANDING_URL,
} from '../../support/publicUrls';

export const LANDING_URL = WHATSAPP_LANDING_URL;
export const LANDING_MARKDOWN_URL = WHATSAPP_LANDING_MARKDOWN_URL;
export const REGISTER_URL = PANEL_LEAD_URL;
export const CONTACT_URL =
  'https://wa.me/5521971792112?text=Ol%C3%A1%2C%20quero%20criar%20um%20agente%20de%20IA%20para%20WhatsApp.';
export const LAST_MODIFIED = '2026-09-29';
export const LAST_MODIFIED_LABEL = '29 de setembro de 2026';

export const SEO = {
  title: 'Inteligência artificial para WhatsApp | Agente de IA da MAIA',
  description:
    'Crie uma IA para WhatsApp que conversa com clientes, responde dúvidas, executa tarefas e transfere a conversa para sua equipe.',
  canonical: LANDING_URL,
  applicationDescription:
    'A MAIA é uma plataforma de IA para criar e operar agentes de inteligência artificial conectados à WhatsApp Business Platform pela API oficial da Meta.',
};

export const HERO = {
  eyebrow: 'Inteligência artificial para WhatsApp',
  headline: 'Converse com seu público usando IA para WhatsApp',
  body:
    'Crie um agente de inteligência artificial para conversar no WhatsApp, responder dúvidas, executar tarefas e chamar sua equipe quando necessário.',
};

export const TRUST = {
  label: 'Integração direta com a Meta',
  detail: 'WhatsApp Business Platform · Cloud API oficial',
};

export const OUTCOMES_SECTION = {
  eyebrow: 'Mais capacidade para sua operação',
  headline: 'O agente cuida do começo. Sua equipe entra onde faz diferença.',
  body:
    'A MAIA mantém cada conversa em movimento com IA para WhatsApp: entende o contato, consulta o conhecimento da empresa e encaminha o próximo passo.',
};

export const OUTCOMES = [
  {
    id: 'answer',
    number: '01',
    title: 'Responda a qualquer hora',
    description:
      'Dúvidas comuns recebem respostas usando o conteúdo e as regras que você definiu.',
  },
  {
    id: 'qualify',
    number: '02',
    title: 'Qualifique enquanto conversa',
    description:
      'O agente coleta as informações necessárias sem transformar o atendimento em um formulário.',
  },
  {
    id: 'act',
    number: '03',
    title: 'Execute o próximo passo',
    description:
      'Habilidades conectadas podem consultar sistemas, registrar dados e acionar tarefas da operação.',
  },
  {
    id: 'handoff',
    number: '04',
    title: 'Transfira com contexto',
    description:
      'Quando uma pessoa precisa assumir, ela recebe a conversa e sabe o que já aconteceu.',
  },
];

export const USE_CASES_SECTION = {
  eyebrow: 'Um canal, diferentes objetivos',
  headline: 'Use uma IA que conversa onde seus clientes já estão',
};

export const USE_CASES = [
  {
    id: 'sales',
    label: 'Vendas',
    title: 'Transforme interesse em oportunidade',
    description:
      'Entenda a necessidade, qualifique o contato e direcione cada oportunidade para o próximo passo.',
    example: '“Busco um plano para uma equipe de 12 pessoas.”',
    response: '“Perfeito. Qual resultado vocês querem melhorar primeiro?”',
  },
  {
    id: 'support',
    label: 'Atendimento',
    title: 'Resolva dúvidas sem deixar ninguém esperando',
    description:
      'Responda perguntas recorrentes com a base da empresa e encaminhe exceções para sua equipe.',
    example: '“Como altero os dados da minha conta?”',
    response: '“Eu te explico agora. Se preferir, também posso chamar o suporte.”',
  },
  {
    id: 'scheduling',
    label: 'Agendamentos',
    title: 'Leve a conversa até uma ação',
    description:
      'Colete preferências, consulte serviços conectados e confirme o que ficou combinado.',
    example: '“Tem um horário amanhã à tarde?”',
    response: '“Vou consultar a agenda. Você prefere depois das 14h ou das 16h?”',
  },
  {
    id: 'education',
    label: 'Conteúdo',
    title: 'Faça seu conhecimento conversar',
    description:
      'Transforme cursos, documentos e métodos em orientação personalizada dentro do WhatsApp.',
    example: '“Em qual etapa do método eu estou?”',
    response: '“Pelo que você já concluiu, o próximo passo é a etapa 3.”',
  },
];

export const HOW_SECTION = {
  eyebrow: 'Como funciona',
  headline: 'Como criar uma inteligência artificial para conversar no WhatsApp',
};

export const STEPS = [
  {
    number: '01',
    title: 'Defina o comportamento',
    description:
      'Escolha o tom, as regras e o que o agente deve fazer em cada tipo de conversa.',
  },
  {
    number: '02',
    title: 'Conecte conhecimento e tarefas',
    description:
      'Adicione seus materiais e as habilidades necessárias para consultar ou atualizar outros sistemas.',
  },
  {
    number: '03',
    title: 'Publique no WhatsApp',
    description:
      'Conecte seu número pela WhatsApp Business Platform e acompanhe a operação pelo painel.',
  },
];

export const CONTROL_SECTION = {
  eyebrow: 'Inteligência artificial e pessoas na mesma operação',
  headline: 'Automação com espaço para sua equipe assumir',
  body:
    'Você acompanha as conversas e define quando o agente deve continuar, pedir ajuda ou transferir o atendimento.',
};

export const CONTROL_POINTS = [
  'Histórico e contexto da conversa preservados',
  'Regras para encaminhamento ao atendimento humano',
  'Conhecimento próprio orientando as respostas',
  'Tarefas e integrações com controle de acesso',
];

export const FAQ_SECTION = {
  eyebrow: 'Dúvidas frequentes',
  headline: 'Inteligência artificial para WhatsApp, sem mistério',
};

export const FAQS = [
  {
    question: 'O que é uma IA para WhatsApp?',
    answer:
      'É uma inteligência artificial que conversa com seus contatos dentro do WhatsApp. A MAIA permite configurar o comportamento, o conhecimento e as tarefas do agente para cada operação.',
  },
  {
    question: 'O agente usa a API oficial do WhatsApp?',
    answer:
      'Sim. A integração é feita com a WhatsApp Business Platform por meio da Cloud API oficial da Meta.',
  },
  {
    question: 'Preciso programar para criar um agente?',
    answer:
      'As configurações principais para criar uma IA que conversa são feitas pelo painel. Integrações específicas podem exigir apoio técnico.',
  },
  {
    question: 'O agente pode usar informações da minha empresa?',
    answer:
      'Sim. Você conecta bases de conhecimento para orientar as respostas com documentos, páginas e outros materiais próprios.',
  },
  {
    question: 'Ele substitui completamente o atendimento humano?',
    answer:
      'Não precisa substituir. Você define quando o agente resolve sozinho e quando a conversa deve seguir para uma pessoa.',
  },
  {
    question: 'O que é um WhatsApp agent?',
    answer:
      'WhatsApp agent é o nome usado em inglês para um agente de inteligência artificial que conversa, atende e executa tarefas pelo WhatsApp.',
  },
  {
    question: 'O agente consegue executar tarefas?',
    answer:
      'Sim. Habilidades e integrações podem permitir consultas e ações em sistemas externos, respeitando as permissões configuradas.',
  },
  {
    question: 'Posso acompanhar as conversas?',
    answer:
      'Sim. As conversas e os contatos ficam disponíveis no painel para acompanhamento da operação.',
  },
];

export const CLOSING = {
  headline: 'Seu próximo atendimento pode começar com a MAIA',
  body:
    'Crie sua IA para WhatsApp, conecte o conhecimento da empresa e publique quando estiver pronto.',
};

export const RELATED_LINKS = [
  {
    href: COMPANY_HOME_URL,
    label: 'Conheça a plataforma de IA',
  },
  {
    href: MAIA_HOME_URL,
    label: 'Transforme seu curso em mentoria digital',
  },
];
